import React, { useEffect } from 'react';
import { useStore } from '../../../../../stores/root';

// assets
import { faCheck, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../services/general/error';
import ValidationService from '../../../../../services/validation';
import GeneralService from '../../../../../services/general';

// props

// components
import { DefaultButton, Dropdown, IDropdownOption, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import Label from '../../../../typography/label';
import SelectSubsidiary from '../../../../uiframeworks/forms/selectSubsidiary';
import { IVesselResourceProps } from '../../../../../props/vessels/vessel';
import VesselService from '../../../../../services/vessels/vessel';
import SelectCountry from '../../../../uiframeworks/forms/data/geographics/country';
import SelectShipType from '../../../../uiframeworks/forms/data/fleets/shipType';
import { IVesselEngineResourceProps } from '../../../../../props/vessels/engine';
import VesselEngineService from '../../../../../services/vessels/engine';
import VesselEngineTypes from '../../../../../manifests/types/vesselEngine';
import SelectVessel from '../../../../uiframeworks/forms/vessels';

interface VesselEngineFormProps {
    vesselId: string;
    engineId?: string;
    onDismissed(refresh?: boolean, status?: string): void;
}

const VesselEngineForm: React.FC<VesselEngineFormProps> = (props: VesselEngineFormProps) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [data, setData] = React.useState<Partial<IVesselEngineResourceProps>>({
        id: GeneralService.guid()
    });
    const [error, setError] = React.useState<any>({});
    const mode = props.engineId ? 'update' : 'create';

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        try {
            let _data = data;

            if (props.engineId) {
                const engine = await VesselEngineService.read(props.vesselId, props.engineId);
                _data = engine;
            } else if (props.vesselId) {
                const vessel = await VesselService.read(props.vesselId);
                _data.vessel = vessel;
            }

            setData({ ..._data });
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'get_vessel_engine_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: `Failed to get vessel engine. Error: ${ErrorService.getMessage(e)}`
            });
            props.onDismissed();
        }
    }

    const isSubmitButtonDisabled = (): boolean => {
        if (!data.vessel || !data.name || !data.type) {
            return true;
        } else if ((data.name || "").trim() === "" || (data.type || "").trim() === "") {
            return true;
        } else if (Object.values(error).filter(Boolean).length > 0) {
            return true;
        }

        return false;
    }

    const _onSubmit = async () => {
        try {
            setSubmitting(true);
            const { name, type, vessel } = data;

            // create form data
            const fd = new FormData();
            fd.append('name', name || "");
            fd.append('type', type || "");

            let engine = undefined;
            if (props.engineId && vessel) {
                engine = await VesselEngineService.update(vessel.id, props.engineId, fd);
            } else if (vessel) {
                engine = await VesselEngineService.create(vessel.id, fd);
            }

            banner.add({
                key: 'request_engine_success',
                variant: 'success',
                icon: faCheck,
                text: `Vessel engine "${name}" has been ${mode}d.`
            });
            props.onDismissed(true);
        } catch (e) {
            setSubmitting(false);
        }
    }

    return <Panel headerText={mode === 'create' ? 'Create Vessel Engine' : 'Update Vessel Engine'}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed(false)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                {
                    !submitting ? (
                        <>
                            <PrimaryButton text={mode === 'create' ? 'Create' : 'Update'} disabled={isSubmitButtonDisabled()} onClick={_onSubmit} />
                            <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                        </>
                    ) : null
                }
                {submitting ? <Spinner size={SpinnerSize.medium} labelPosition={"right"} label={mode === 'create' ? "Creating vessel engine ..." : "Updating vessel engine ..."} /> : null}
            </Stack>;
        }}>
        <Stack tokens={{ childrenGap: 20 }}>
            {!loaded ? <Stack horizontalAlign={"baseline"}><Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Preparing form ..."} /></Stack> : null}
            {
                loaded ? <>
                    <SelectVessel label={"Vessel"}
                        selected={data.vessel}
                        required
                        onChange={(selected) => {
                            let _data = data;
                            _data.vessel = selected;

                            const validation = ValidationService.combination(selected, ['required'], {});
                            error.vessel = validation.message;

                            setData({ ...data });
                            setError({ ...error });
                        }} />
                    <Stack className={'divider'} />
                    <TextField label={"Name"}
                        value={data.name}
                        required
                        disabled={submitting}
                        onChange={(evt: any, value?: string) => {
                            let _data = data;
                            _data.name = value;

                            const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 100 });
                            error.name = validation.message;

                            setData({ ...data });
                            setError({ ...error });
                        }}
                        errorMessage={error.name} />
                    <Dropdown label={"Type"}
                        options={VesselEngineTypes}
                        selectedKey={data.type}
                        required
                        disabled={submitting}
                        onChange={(evt: any, opt?: IDropdownOption) => {
                            let _data = data;
                            _data.type = opt?.key as string;

                            const validation = ValidationService.combination(_data.type, ['required'], {});
                            error.type = validation.message;

                            setData({ ...data });
                            setError({ ...error });
                        }}
                        errorMessage={error.type} />
                </> : null
            }
        </Stack>
    </Panel>
};

export default VesselEngineForm;
